/**
 * Distance tab — tag vocabulary, thresholds and palette.
 *
 * The thresholds mirror the backend rule described in
 * `docs/trip-distance-tab/TRIP_DISTANCE_TAB.md` §4.6. They live on the client only so the
 * tab can show an honest, clearly-marked *provisional* tag while `gps.coveragePercent` is
 * null — i.e. before the reconciliation API ships. Once the backend returns `source_tag`,
 * that value wins and nothing below is consulted.
 */

/** GPS coverage at or above this share of the trip window → GPS Verified. */
export const COVERAGE_VERIFIED_PCT = 90;
/** Below this → Odometer Only; between the two → GPS Partial. */
export const COVERAGE_PARTIAL_PCT = 30;
/** |GPS − Odometer| / Odometer above this → high-variance badge (an overlay, not a tag). */
export const VARIANCE_FLAG_PCT = 15;

/**
 * Allowance on top of the planned distance before the final distance counts as over-run.
 * Threshold distance = planned × (1 + this/100) — a route-adherence check, entirely
 * separate from GPS-vs-odometer variance: variance asks whether the two measurements agree,
 * the threshold asks whether the trip stayed close to the route it was quoted on.
 */
export const THRESHOLD_BUFFER_PCT = 10;

/** A halt outside any node lasting at least this long is "unscheduled", not a traffic stop. */
export const UNSCHEDULED_HALT_MINUTES = 30;

export const DISTANCE_SOURCE_TAG = {
	GPS_VERIFIED: 'GPS_VERIFIED',
	GPS_PARTIAL: 'GPS_PARTIAL',
	ODOMETER_ONLY: 'ODOMETER_ONLY',
	MANUAL_OVERRIDE: 'MANUAL_OVERRIDE',
	NO_SOURCE: 'NO_SOURCE',
};

/**
 * One entry per tag: the chip's colours and the plain-English condition it stands for.
 * `condition` is shown under the tag so nobody has to look the rule up elsewhere.
 */
export const TAG_META = {
	[DISTANCE_SOURCE_TAG.GPS_VERIFIED]: {
		label: 'GPS Verified',
		dot: '#16a34a',
		color: '#15803d',
		background: '#f0fdf4',
		border: '#bbf7d0',
		condition: `GPS covered ${COVERAGE_VERIFIED_PCT}% or more of the trip duration.`,
	},
	[DISTANCE_SOURCE_TAG.GPS_PARTIAL]: {
		label: 'GPS Partial + Odometer Fallback',
		dot: '#f59e0b',
		color: '#b45309',
		background: '#fffbeb',
		border: '#fde68a',
		condition: `GPS covered between ${COVERAGE_PARTIAL_PCT}% and ${COVERAGE_VERIFIED_PCT}% of the trip — odometer used for billing.`,
	},
	[DISTANCE_SOURCE_TAG.ODOMETER_ONLY]: {
		label: 'Odometer Only',
		dot: '#fa8c16',
		color: '#c2410c',
		background: '#fff7ed',
		border: '#fed7aa',
		condition: `GPS covered under ${COVERAGE_PARTIAL_PCT}% of the trip, or the vehicle has no GPS hardware.`,
	},
	[DISTANCE_SOURCE_TAG.MANUAL_OVERRIDE]: {
		label: 'Manual Override',
		dot: '#ff4d4f',
		color: '#b91c1c',
		background: '#fff1f0',
		border: '#fecaca',
		condition:
			'Distance was entered on Logitrack rather than derived from GPS or the driver app.',
	},
	[DISTANCE_SOURCE_TAG.NO_SOURCE]: {
		label: 'No Distance Source',
		dot: '#94a3b8',
		color: '#475569',
		background: '#f8fafc',
		border: '#e2e8f0',
		condition: 'Neither GPS nor odometer produced a usable distance for this trip.',
	},
};

export const HALT_TYPE = {
	DWELL: 'dwell',
	IN_TRANSIT: 'in_transit',
	UNSCHEDULED_LONG: 'unscheduled_long',
};

export const HALT_META = {
	[HALT_TYPE.DWELL]: {
		label: 'Dwell',
		description: 'Stationary inside a node geofence — loading, unloading or paperwork.',
		// The Flag column answers "does this halt need someone to look at it".
		flag: 'Expected',
		flagColor: '#16a34a',
		flagBackground: '#f0fdf4',
	},
	[HALT_TYPE.IN_TRANSIT]: {
		label: 'In-Transit Halt',
		description: `Stationary outside any node for under ${UNSCHEDULED_HALT_MINUTES} minutes — traffic or a driver break.`,
		flag: 'Normal',
		flagColor: '#64748b',
		flagBackground: '#f1f5f9',
	},
	[HALT_TYPE.UNSCHEDULED_LONG]: {
		label: 'Unscheduled Long Halt',
		description: `Stationary outside any node for ${UNSCHEDULED_HALT_MINUTES} minutes or more — flagged for Ops review.`,
		flag: 'Review',
		flagColor: '#b91c1c',
		flagBackground: '#fff1f0',
	},
};

/**
 * The stoppage proportion bar. `unmeasured` is the honest remainder: trip time that is
 * neither confirmed moving nor confirmed stopped, which is most of it until high-resolution
 * ping history exists. Rendering that as "moving" would be the easy lie.
 */
export const STOPPAGE_BAR = {
	moving: { label: 'Moving', color: '#4263eb' },
	dwell: { label: 'Dwell', color: '#7950f2' },
	halt: { label: 'Halt', color: '#b8860b' },
	unscheduled: { label: 'Unscheduled', color: '#dc2626' },
	unmeasured: { label: 'Unmeasured', color: '#cbd5e1' },
};

/**
 * Who supplied the closing odometer reading — `trip_distance_reconciliation.end_km_source`.
 * `admin_manual` is what drives the 🔴 Manual Override tag, so naming it plainly on the
 * odometer card is the difference between "the system disagreed with itself" and
 * "a person typed this in".
 */
export const END_KM_SOURCE_LABEL = {
	driver_app: 'Driver app',
	admin_manual: 'Entered manually on Logitrack',
	gps_auto: 'Auto-closed by GPS',
};

/** Dark table header, matching the one TripProgressSection already uses. */
export const TABLE_HEADER_BG = '#1f3e5a';

export const TIMELINE_SEGMENT = {
	ACTIVE: 'active',
	GAP: 'gap',
};

export const TIMELINE_COLOR = {
	[TIMELINE_SEGMENT.ACTIVE]: '#00c853',
	[TIMELINE_SEGMENT.GAP]: '#ff4d4f',
	UNKNOWN: '#e2e8f0',
};

/**
 * `search_type: 'trip_distance_reconciliation'` — live as of the backend's
 * feat/trip-distance-optim branch. Set to false to fall the whole tab back to
 * `all_tracking_info` alone (planned/GPS/odometer/final still render; coverage, the
 * health timeline and the halt breakdown go quiet) if the endpoint ever has to be
 * disabled on an environment.
 */
export const USE_RECONCILIATION_API = true;

/** Only ever polled while the trip is open; a closed trip's numbers are final. */
export const POLL_INTERVAL_MS = 30000;

/** trips_v2.trip_status_id values that mean "this trip is over". */
export const TERMINAL_TRIP_STATUS_IDS = [3, 9];
